import React, { useState, useEffect, useRef, useCallback } from 'react'
import './chatbot.css'
import logoWhite from '../../Assets/logo_white.svg'
import { COLLECTION_NAME, GLOBAL_DOMAIN } from '../../Config/Config'

// ─── Config ──────────────────────────────────────────────────────────────────
const API_URL = process.env.REACT_APP_PUBLIC_VAI_API_URL

// Generate a stable session ID for this browser session (cwgt_{timestamp}_{counter})
let _sessionCounter = 1
function makeSessionId() {
  return `cwgt_${Date.now()}_${_sessionCounter++}`
}

// One session ID per page load — shared across all messages
const SESSION_ID = makeSessionId()

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'bot',
  text: "Hi! I'm VAI, your ATUM Life assistant 👋\nHow can I help you today?",
  time: new Date(),
}

const QUICK_REPLIES = [
  'Track my order',
  'Browse products',
  'Store locations',
  'Contact support',
]

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function makeId() {
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
}

// ─── API Call ─────────────────────────────────────────────────────────────────
async function fetchBotReply(userMessage, { signal, locationPath }) {
  if (!API_URL) {
    throw new Error(
      'API URL not configured. Set REACT_APP_PUBLIC_VAI_API_URL in .env',
    )
  }

  const payload = {
    prompt: userMessage,
    session_id: SESSION_ID,
    source_id: 'ai.visakaservices.com',
    page_url: locationPath,
    model: 'vai - custom: latest',
    collection_name: COLLECTION_NAME,
  }

  const response = await fetch(API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
    signal,
  })

  if (!response.ok) {
    let errBody = null
    try {
      errBody = await response.json()
    } catch (_) {
      try {
        errBody = await response.text()
      } catch (_) {}
    }
    console.error(`[VAI] HTTP ${response.status} — payload sent:`, payload)
    console.error('[VAI] Error body from server:', errBody)
    throw new Error(`Server responded with ${response.status}`)
  }

  const data = await response.json()

  function extractText(obj) {
    if (!obj || typeof obj !== 'object') return null

    const keys = [
      'content',
      'reply',
      'message',
      'response',
      'text',
      'answer',
      'output',
      'result',
      'data',
      'body',
      'msg',
    ]

    for (const k of keys) {
      const val = obj[k]

      if (typeof val === 'string' && val.trim()) return val.trim()

      if (Array.isArray(val)) {
        for (const item of val) {
          const found = typeof item === 'string' ? item : extractText(item)
          if (found) return found
        }
      }

      if (val && typeof val === 'object') {
        const found = extractText(val)
        if (found) return found
      }
    }

    return null
  }

  const reply = extractText(data)

  return reply ?? "I'm sorry, I didn't quite get that. Could you rephrase?"
}

// ─── Markdown renderer ────────────────────────────────────────────────────────
function renderMarkdown(text) {
  const lines = String(text ?? '').split('\n')
  const elements = []
  let key = 0

  lines.forEach((line, i) => {
    const trimmed = line.trim()

    const numMatch = trimmed.match(/^(\d+)\.\s+(.*)$/)
    const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/)

    const raw = numMatch ? numMatch[2] : bulletMatch ? bulletMatch[1] : trimmed

    const inlineNodes = []
    const inlineRe = /(\*\*(.+?)\*\*|\*(.+?)\*)/g
    let last = 0
    let m

    while ((m = inlineRe.exec(raw)) !== null) {
      if (m.index > last) inlineNodes.push(raw.slice(last, m.index))
      if (m[2]) inlineNodes.push(<strong key={key++}>{m[2]}</strong>)
      else if (m[3]) inlineNodes.push(<em key={key++}>{m[3]}</em>)
      last = m.index + m[0].length
    }

    if (last < raw.length) inlineNodes.push(raw.slice(last))

    if (numMatch) {
      elements.push(
        <div
          key={key++}
          style={{ display: 'flex', gap: '6px', margin: '3px 0' }}
        >
          <span style={{ color: '#229986', fontWeight: 700, minWidth: '18px' }}>
            {numMatch[1]}.
          </span>
          <span>{inlineNodes}</span>
        </div>,
      )
    } else if (bulletMatch) {
      elements.push(
        <div
          key={key++}
          style={{ display: 'flex', gap: '6px', margin: '3px 0' }}
        >
          <span style={{ color: '#229986', fontWeight: 700 }}>•</span>
          <span>{inlineNodes}</span>
        </div>,
      )
    } else if (trimmed === '') {
      if (i > 0) elements.push(<div key={key++} style={{ height: '6px' }} />)
    } else {
      elements.push(<span key={key++}>{inlineNodes}</span>)
      if (i < lines.length - 1) elements.push(<br key={key++} />)
    }
  })

  return elements
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zm-2 10H6v-2h12v2zm0-4H6V6h12v2z" />
  </svg>
)

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    fill="white"
    stroke="white"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const SendIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
)

const BotAvatarIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7H4a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zM7.5 14a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM4 19h16v2H4v-2z" />
  </svg>
)

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ChatLauncher() {
  const [open, setOpen] = useState(false)
  const [closing, setClosing] = useState(false)
  const [messages, setMessages] = useState([WELCOME_MESSAGE])
  const [inputText, setInputText] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [hasUnread, setHasUnread] = useState(true)
  const [error, setError] = useState(null)

  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const abortRef = useRef(null)

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300)
      setHasUnread(false)
    }
  }, [open])

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  const handleToggle = useCallback(() => {
    if (open) {
      setClosing(true)
      setTimeout(() => {
        setOpen(false)
        setClosing(false)
      }, 180)
    } else {
      setOpen(true)
    }
  }, [open])

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim()
      if (!trimmed || isTyping) return

      setError(null)

      const userMsg = {
        id: makeId(),
        role: 'user',
        text: trimmed,
        time: new Date(),
      }

      setMessages((prev) => [...prev, userMsg])
      setInputText('')
      setIsTyping(true)

      abortRef.current?.abort()
      abortRef.current = new AbortController()

      try {
        const reply = await fetchBotReply(trimmed, {
          signal: abortRef.current.signal,
          locationPath: window.location.href,
        })

        const botMsg = {
          id: makeId(),
          role: 'bot',
          text: reply,
          time: new Date(),
        }

        setMessages((prev) => [...prev, botMsg])
      } catch (err) {
        if (err?.name === 'AbortError') return
        console.error('[VAI]', err)
        setError("Sorry, I couldn't reach the server. Please try again.")
      } finally {
        setIsTyping(false)
      }
    },
    [isTyping],
  )

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(inputText)
    }
  }

  return (
    <>
      {open && (
        <div
          className={`vai-window${closing ? ' vai-closing' : ''}`}
          role="dialog"
          aria-label="ATUM Assistant"
        >
          <div className="vai-header">
            <div className="vai-header-avatar">
              <img
                src={logoWhite}
                alt="ATUM"
                onError={(e) => {
                  e.target.style.display = 'none'
                }}
              />
            </div>

            <div className="vai-header-info">
              <p className="vai-header-title">ATUM Assistant</p>
              <p className="vai-header-status">
                <span className="vai-status-dot" />
                {isTyping ? 'Typing…' : 'Online — ready to help'}
              </p>
            </div>

            <button
              className="vai-header-close"
              onClick={handleToggle}
              aria-label="Close chat"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="vai-messages" role="log" aria-live="polite">
            {messages.map((msg) => (
              <div key={msg.id} className={`vai-msg ${msg.role}`}>
                <div className="vai-bubble">
                  {msg.role === 'bot'
                    ? renderMarkdown(msg.text)
                    : String(msg.text ?? '')}
                </div>
                <span className="vai-msg-time">{formatTime(msg.time)}</span>
              </div>
            ))}

            {isTyping && (
              <div className="vai-typing" aria-label="VAI is typing">
                <span />
                <span />
                <span />
              </div>
            )}

            {error && <div className="vai-error">⚠ {error}</div>}

            {messages.length === 1 && !isTyping && (
              <div className="vai-quick-replies">
                {QUICK_REPLIES.map((q) => (
                  <button
                    key={q}
                    className="vai-quick-chip"
                    onClick={() => sendMessage(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="vai-input-area">
            <textarea
              ref={inputRef}
              className="vai-input"
              placeholder="Type a message…"
              value={inputText}
              rows={1}
              onChange={(e) => {
                setInputText(e.target.value)
                e.target.style.height = 'auto'
                const newHeight = Math.min(e.target.scrollHeight, 96)
                e.target.style.height = `${newHeight}px`
                e.target.style.overflowY =
                  e.target.scrollHeight > 96 ? 'auto' : 'hidden'
              }}
              onKeyDown={handleKeyDown}
              aria-label="Message input"
            />
            <button
              className="vai-send-btn"
              onClick={() => sendMessage(inputText)}
              disabled={!inputText.trim() || isTyping}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>

          <div className="vai-footer-brand">
            Powered by <span>VAI · Visaka AI</span>
          </div>
        </div>
      )}

      <button
        className="vai-toggle-btn"
        onClick={handleToggle}
        aria-label={open ? 'Close chat' : 'Open chat'}
        aria-expanded={open}
      >
        {open ? (
          <CloseIcon />
        ) : (
          <>
            <ChatIcon />
            {hasUnread && (
              <span className="vai-badge" aria-label="New message" />
            )}
          </>
        )}
      </button>
    </>
  )
}
